/**
 * OrderCard Component
 *
 * Displays a client's order card with:
 * - Order details (title, description, location, date)
 * - For PUBLIC orders: "Responses" button with unread badge
 * - For DIRECT orders: Worker avatar and name
 * - Delete button in top-right corner
 */

'use client';

import { FC, useState, useRef, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { MoreHorizontal, ExternalLink, XCircle } from 'lucide-react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';
import { OrderReviewStars } from '@/src/features/reviews';
import { cn } from '@/src/shared/lib/utils';
import { OrderStatusBadge } from '@/src/shared/ui/badges/OrderStatusBadge';
import type { OrderResponse } from '@/src/shared/api/orders.api';

export interface OrderCardProps {
  order: OrderResponse;
  proposalsCount?: number;
  unreadProposalsCount?: number;
  onDelete?: (orderId: string) => void;
  onCancel?: (orderId: string) => void;
  onClick?: (orderId: string) => void;
  onComplete?: (orderId: string) => void;
  onAccept?: (orderId: string) => void;
  onRenew?: (orderId: string) => void;
  onRestore?: (orderId: string) => void;
}

function daysUntilArchive(publishedAt?: string | null, lastActivityAt?: string | null): number {
  const anchor = lastActivityAt || publishedAt;
  if (!anchor) return 7;
  const elapsed = Math.floor((Date.now() - new Date(anchor).getTime()) / 86_400_000);
  return Math.max(0, 7 - elapsed);
}

function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export const OrderCard: FC<OrderCardProps> = ({
  order,
  proposalsCount = 0,
  unreadProposalsCount = 0,
  onDelete,
  onCancel,
  onClick,
  onComplete,
  onAccept,
  onRenew,
  onRestore,
}) => {
  const t = useTranslations('profile.orders');
  const locale = useLocale();
  const router = useRouter();
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const cancellableStatuses = ['DRAFT', 'PUBLISHED', 'IN_PROGRESS'];
  const canCancel = cancellableStatuses.includes(order.status);

  const isPublicOrder = order.type === 'PUBLIC';
  const isDirectOrder = order.type === 'DIRECT';
  const hasWorker = !!order.workerActor;

  const handleCardClick = () => {
    if (onClick) {
      onClick(order.id);
    }
  };

  const handleResponsesClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (isPublicOrder) {
      router.push(`/profile/orders/${order.id}`);
    }
  };

  const handleWorkerClick = () => {
    if (hasWorker && order.workerActor?.id) {
      router.push(`/profile/worker/${order.workerActor.id}`);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      setIsDeleting(true);
      onDelete(order.id);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onCancel) {
      onCancel(order.id);
    }
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    router.push(`/${locale}/profile/orders/${order.id}`);
  };

  const handleComplete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onComplete) {
      onComplete(order.id);
    }
  };

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAccept) onAccept(order.id);
  };

  const handleRenew = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRenew) onRenew(order.id);
  };

  const handleRestore = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRestore) onRestore(order.id);
  };

  const location = order.city
    ? `${order.city}${order.province ? `, ${order.province}` : ''}`
    : order.address || '';

  return (
    <div
      onClick={handleCardClick}
      className="relative w-full p-5 bg-background-primary rounded-[20px] border border-accent-secondary flex flex-col gap-4 cursor-pointer hover:shadow-md transition-shadow"
    >
      {/* Context menu - top right corner */}
      <div
        className="absolute top-4 right-4"
        ref={menuRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
          className="p-2 text-text-tertiary hover:text-accent-primary hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Order actions"
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-border-primary rounded-xl shadow-lg z-50 overflow-hidden">
            <button
              onClick={handleOpen}
              className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-text-primary hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="w-4 h-4 text-text-tertiary" />
              Open
            </button>
            {(onCancel || onDelete) && (
              <button
                onClick={canCancel ? (onCancel ? handleCancel : handleDelete) : undefined}
                disabled={!canCancel || isDeleting}
                title={!canCancel ? 'Not available at this status' : undefined}
                className={cn(
                  'flex items-center gap-2.5 w-full px-4 py-2.5 text-sm transition-colors',
                  canCancel && !isDeleting
                    ? 'text-feedback-error hover:bg-red-50 cursor-pointer'
                    : 'text-text-tertiary opacity-50 cursor-not-allowed'
                )}
              >
                <XCircle className="w-4 h-4" />
                Cancel order
              </button>
            )}
          </div>
        )}
      </div>

      {/* Order info section */}
      <div className="flex flex-col gap-2.5 pr-10">
        {/* Title + Status badge */}
        <div className="flex items-start justify-between gap-2">
          <Typography variant="h3" weight="bold" color="primary">
            {order.title}
          </Typography>
          <OrderStatusBadge status={order.status} className="shrink-0 mt-0.5" />
        </div>

        {/* Review Stars - show for COMPLETED orders with worker */}
        {order.status === 'COMPLETED' && hasWorker && (
          <OrderReviewStars
            orderId={order.id}
            userRole="client"
            orderTitle={order.title}
            workerName={order.workerActor?.displayName || order.workerActor?.name}
            workerAvatarUrl={order.workerActor?.avatarUrl}
          />
        )}

        {/* Location and date */}
        <div className="flex items-center justify-between">
          {location && (
            <Typography variant="bodySm" color="secondary">
              {location}
            </Typography>
          )}
          <Typography variant="bodySm" color="tertiary">
            {order.publishedAt && (order.status === 'PUBLISHED' || order.status === 'ARCHIVED')
              ? formatDate(order.publishedAt, locale)
              : formatDate(order.createdAt, locale)}
          </Typography>
        </div>

        {/* Description */}
        <Typography variant="body" color="secondary" className="line-clamp-2">
          {order.description}
        </Typography>

        {/* Budget */}
        {order.budget && (
          <Typography variant="body" weight="medium" color="primary">
            {t('budget')}: ${order.budget}
          </Typography>
        )}
      </div>

      {/* Divider */}
      <div className="w-full h-px bg-accent-secondary" />

      {/* Bottom section - PUBLIC vs DIRECT */}
      {/* Show "Responses" button only for PUBLISHED PUBLIC orders */}
      {isPublicOrder && order.status === 'PUBLISHED' && (
        <div className="flex flex-col gap-3">
          {/* Responses button */}
          <PrimaryButton
            onClick={handleResponsesClick}
            fullWidth
            className="relative"
          >
            <span className="flex items-center justify-center gap-2">
              <Typography variant="body" weight="medium" color="inherit">
                {proposalsCount === 0
                  ? t('noNewResponses')
                  : t('responses', { count: proposalsCount })}
              </Typography>
              {unreadProposalsCount > 0 && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full">
                  {unreadProposalsCount}
                </span>
              )}
            </span>
          </PrimaryButton>

          {/* Renew button — resets 7-day inactivity timer */}
          {onRenew && (() => {
            const days = daysUntilArchive(order.publishedAt, order.lastActivityAt);
            const isUrgent = days <= 1;
            return (
              <PrimaryButton
                onClick={handleRenew}
                fullWidth
                className={isUrgent
                  ? 'bg-transparent border border-red-500 text-red-500 hover:bg-red-500 hover:text-white'
                  : 'bg-transparent border border-accent-primary text-accent-primary hover:bg-accent-primary hover:text-white'}
              >
                <Typography variant="body" weight="medium" color="inherit">
                  {t('renew')} · {days === 0 ? t('archivesToday') : days === 1 ? t('archivesTomorrow') : t('archivesInDays', { days })}
                </Typography>
              </PrimaryButton>
            );
          })()}
        </div>
      )}

      {/* Restore button for ARCHIVED orders */}
      {order.status === 'ARCHIVED' && onRestore && (
        <div className="flex flex-col gap-3">
          <PrimaryButton onClick={handleRestore} fullWidth>
            <Typography variant="body" weight="medium" color="inherit">
              {t('restore')}
            </Typography>
          </PrimaryButton>
        </div>
      )}

      {/* Show worker info for DIRECT orders or PUBLIC orders with assigned worker */}
      {hasWorker && (isDirectOrder || (isPublicOrder && order.status !== 'PUBLISHED')) && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Avatar
              src={order.workerActor?.avatarUrl}
              alt={order.workerActor?.displayName || order.workerActor?.name || 'Worker'}
              size="md"
              fallback={(order.workerActor?.name || 'W').charAt(0)}
            />
            <div className="flex-1">
              <button
                onClick={handleWorkerClick}
                className="text-left hover:opacity-80 transition-opacity"
              >
                <Typography variant="body" weight="medium" color="primary">
                  {order.workerActor?.displayName || order.workerActor?.name}
                </Typography>
                <Typography variant="bodySm" color="secondary">
                  {t('assignedWorker')}
                </Typography>
              </button>
            </div>
          </div>

          {/* Accept button for DIRECT orders awaiting client confirmation */}
          {order.status === 'PUBLISHED' && onAccept && (
            <PrimaryButton onClick={handleAccept} fullWidth>
              <Typography variant="body" weight="medium" color="inherit">
                {t('acceptOrder')}
              </Typography>
            </PrimaryButton>
          )}

          {/* Complete order button for PENDING_REVIEW status */}
          {order.status === 'PENDING_REVIEW' && onComplete && (
            <PrimaryButton
              onClick={handleComplete}
              fullWidth
              className="bg-green-600 hover:bg-green-700"
            >
              <Typography variant="body" weight="medium" color="inherit">
                {t('completeOrder')}
              </Typography>
            </PrimaryButton>
          )}
        </div>
      )}

      {isDirectOrder && !hasWorker && (
        <Typography variant="body" color="tertiary" className="text-center">
          {t('noWorkerAssigned')}
        </Typography>
      )}
    </div>
  );
};
